// Intent: one chat UI for both sides of the APP channel — the client screen
// (/chat, me=VISITOR) and the agent-mode pane (/agent-chat/[id], me=AGENT).
// Presentational only: polling and sending live in the screens, so each side
// keeps its own API surface and cadence.

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Keyboard, KeyboardAvoidingView, Platform, Pressable, ScrollView, TextInput, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { ChatMessage } from '@/api/mobile-api';
import { AppText } from '@/components/ui/app-text';
import { colors, fonts, radius, spacing } from '@/theme/tokens';

function timeOf(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function ChatThread({
  messages,
  me,
  onSend,
  sendDisabled,
  initialDraft,
}: {
  messages: ChatMessage[];
  me: 'VISITOR' | 'AGENT';
  onSend: (body: string) => Promise<void>;
  sendDisabled?: boolean;
  /** Pre-filled composer text (e.g. goal upsell CTA) — the USER still hits send. */
  initialDraft?: string;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(initialDraft ?? '');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<ScrollView>(null);

  useEffect(() => {
    // New message -> stick to the bottom, like every messenger.
    scrollRef.current?.scrollToEnd({ animated: true });
  }, [messages.length]);

  useEffect(() => {
    // Keyboard opens -> keep the latest messages (and the composer) in view.
    const sub = Keyboard.addListener('keyboardDidShow', () => {
      scrollRef.current?.scrollToEnd({ animated: true });
    });
    return () => sub.remove();
  }, []);

  const send = async () => {
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    setDraft('');
    await onSend(body);
    setSending(false);
  };

  return (
    // Without this the keyboard covered the composer row (user: "pole do
    // wpisania tekstu jest poniżej guzików") — same pattern as policy/new.tsx.
    <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
      <ScrollView
        ref={scrollRef}
        style={{ flex: 1 }}
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{ padding: spacing.screen, gap: spacing.sm }}
      >
        {messages.length === 0 ? (
          <AppText variant="bodyMd" color={colors.textSecondary} style={{ textAlign: 'center' }}>
            {t('chat.empty')}
          </AppText>
        ) : null}
        {messages.map((m) => {
          if (m.author === 'SYSTEM') {
            return (
              <AppText
                key={m.id}
                variant="labelMd"
                color={colors.outline}
                style={{ textAlign: 'center' }}
              >
                {m.body}
              </AppText>
            );
          }
          const mine = m.author === me;
          return (
            <View key={m.id} style={{ alignItems: mine ? 'flex-end' : 'flex-start' }}>
              <View
                style={{
                  maxWidth: '85%',
                  backgroundColor: mine ? colors.navy : colors.card,
                  borderRadius: radius.card,
                  paddingHorizontal: spacing.md,
                  paddingVertical: spacing.sm,
                }}
              >
                <AppText variant="bodyLg" color={mine ? '#FFFFFF' : colors.textPrimary}>
                  {m.body}
                </AppText>
              </View>
              <AppText variant="labelMd" color={colors.outlineVariant}>
                {timeOf(m.createdAt)}
              </AppText>
            </View>
          );
        })}
      </ScrollView>

      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: spacing.xs,
          padding: spacing.sm,
          borderTopWidth: 1,
          borderTopColor: colors.divider,
          backgroundColor: colors.background,
        }}
      >
        <TextInput
          value={draft}
          onChangeText={setDraft}
          placeholder={t('chat.placeholder')}
          placeholderTextColor={colors.outline}
          editable={!sendDisabled}
          multiline
          style={{
            flex: 1,
            fontFamily: fonts.medium,
            fontSize: 16,
            color: colors.textPrimary,
            backgroundColor: colors.card,
            borderRadius: radius.button,
            paddingHorizontal: spacing.sm,
            paddingVertical: spacing.xs,
            minHeight: 44,
            maxHeight: 120,
          }}
          accessibilityLabel={t('chat.placeholder')}
        />
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={t('chat.send')}
          disabled={sendDisabled || sending || !draft.trim()}
          onPress={send}
          style={{
            width: 44,
            height: 44,
            borderRadius: radius.full,
            backgroundColor: draft.trim() && !sendDisabled ? colors.primary : colors.outlineVariant,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Ionicons name="send" size={18} color="#FFFFFF" />
        </Pressable>
      </View>
    </KeyboardAvoidingView>
  );
}
